import $global from '@xt/client/store/global'
import type { ClasshourItem } from '@xt/client/entities/course-v2/types.d'
import type { Asset, Material, BaseTemplate, TopDirClasshourLayout, BottomDirClasshourLayout } from '../types.d'

export class TemplateFive implements BaseTemplate {
  /**
   * 当前课程进度 0 - 3
   */
  private taskSchedule: number
  /**
   * 头部课程(开学见面)是否已经发布
   */
  private withStartCourse: boolean
  /**
   * 尾部课程(小灶课)是否已经发布
   */
  private withEndCourse: boolean
  /**
   * 展示学习回顾
   */
  private showReview: boolean
  /**
   * 展示开课前请准备 与 学习回顾位置互斥
   */
  public showPrepare: boolean
  /**
   * 当前真实的课程进度索引，与taskSchedule相比 taskSchedule不会返回小灶课尾部课程的索引
   * 模版5  realCurrent: 0 - 6
   */
  public realCurrent: number

  constructor(options: {
    taskSchedule: number
    showReview: boolean
    showPrepare: boolean
    withStartCourse: boolean
    withEndCourse: boolean
    realCurrent: number
  }) {
    this.taskSchedule = options.taskSchedule
    this.showReview = options.showReview
    this.showPrepare = options.showPrepare
    this.withStartCourse = options.withStartCourse
    this.withEndCourse = options.withEndCourse
    this.realCurrent = options.realCurrent
  }

  private static assets: Record<string, Asset> = {
    'bg-style-v1': {
      url: `${$global.static}/images/course/pc/template/tpl-5/tpl-5-bg-style-v1.png`,
      width: 1199,
      height: 392
    },
    'bg-style-v2': {
      url: `${$global.static}/images/course/pc/template/tpl-5/tpl-5-bg-style-v2.png`,
      width: 1199,
      height: 392
    },
    'bg-style-v3': {
      url: `${$global.static}/images/course/pc/template/tpl-5/tpl-5-bg-style-v3.png`,
      width: 1199,
      height: 392
    },
    'bg-style-v4': {
      url: `${$global.static}/images/course/pc/template/tpl-5/tpl-5-bg-style-v4.png`,
      width: 1199,
      height: 392
    },
    'bg-style-v5': {
      url: `${$global.static}/images/course/pc/template/tpl-5/tpl-5-bg-style-v5-1.png`,
      width: 1199,
      height: 392
    },
    'bg-style-v6': {
      url: `${$global.static}/images/course/pc/template/tpl-5/tpl-5-bg-style-v6-2.png`,
      width: 1199,
      height: 392
    },
    'bg-style-v7': {
      url: `${$global.static}/images/course/pc/template/tpl-5/tpl-5-bg-style-v7.png`,
      width: 1202,
      height: 395
    },
    'bg-style-v8': {
      url: `${$global.static}/images/course/pc/template/tpl-5/tpl-5-bg-style-v8-1.png`,
      width: 1191,
      height: 395
    },
    'material-sprite': {
      url: `${$global.static}/images/course/pc/template/tpl-5/tpl-5-sprites-v1.png`,
      width: 240,
      height: 190
    },
    'material-review': {
      url: `${$global.static}/images/course/pc/bg-review-v1.png`,
      width: 248,
      height: 218
    }
  } as const

  public getTemplateSize() {
    return {
      width: 1199,
      height: 772,
      marginTop: 65,
      // 83
      marginBottom: 0,
      marginLeft: 0
    }
  }

  public getTopMaterialConfigList(): Array<Array<Material>> {
    return [
      [
        {
          type: 'sprite',
          ...TemplateFive.assets['material-sprite'],
          visible: this.showPrepare,
          name: 'bg-tpl_5_prepare',
          top: 0,
          left: 1114,
          x: -89,
          y: -5,
          hover: {
            x: -5,
            y: -5
          },
          clickEventName: 'click-prepare',
          self: {
            width: 74,
            height: 180
          }
        },
        {
          type: 'sprite',
          ...TemplateFive.assets['material-review'],
          visible: this.showReview,
          name: 'bg-review',
          top: -51,
          left: 1080,
          x: -129,
          y: -5,
          hover: {
            x: -5,
            y: -5
          },
          clickEventName: 'click-review',
          self: {
            width: 114,
            height: 208
          }
        }
      ]
    ]
  }

  public getBottomMaterialConfigList(): Array<Array<Material>> {
    // 数字角标 【top, left】
    const numberIndex = [
      [259, 175],
      [234, 401],
      [222, 843],
      [540, 1037],
      [573, 652],
      [533, 309],
      [442, 107]
    ]

    const getBgAsset: () => Material = () => {
      if (this.withStartCourse && this.taskSchedule === 0) {
        return {
          type: 'normal',
          ...TemplateFive.assets['bg-style-v1'],
          visible: true,
          name: 'bg',
          top: 204,
          left: 0
        }
      }

      // 未开课 开学见面也没有发布
      if (this.taskSchedule === 0) {
        return {
          type: 'normal',
          ...TemplateFive.assets['bg-style-v7'],
          visible: true,
          name: 'bg',
          top: 204,
          left: 0
        }
      }

      if (this.taskSchedule === 1) {
        return {
          type: 'normal',
          ...TemplateFive.assets['bg-style-v2'],
          visible: true,
          name: 'bg',
          top: 204,
          left: 0
        }
      }

      if (this.taskSchedule === 2) {
        return {
          type: 'normal',
          ...TemplateFive.assets['bg-style-v3'],
          visible: true,
          name: 'bg',
          top: 204,
          left: 0
        }
      }

      if (this.taskSchedule === 3) {
        return {
          type: 'normal',
          ...TemplateFive.assets['bg-style-v4'],
          visible: true,
          name: 'bg',
          top: 204,
          left: 0
        }
      }

      if (this.taskSchedule === 4) {
        return {
          type: 'normal',
          ...TemplateFive.assets['bg-style-v5'],
          visible: true,
          name: 'bg',
          top: 204,
          left: 0
        }
      }

      if (this.taskSchedule === 5) {
        if (!this.withEndCourse) {
          return {
            type: 'normal',
            ...TemplateFive.assets['bg-style-v8'],
            visible: true,
            name: 'bg',
            top: 204,
            left: 0
          }
        } else {
          return {
            type: 'normal',
            ...TemplateFive.assets['bg-style-v6'],
            visible: true,
            name: 'bg',
            top: 204,
            left: 0
          }
        }
      }

      // never
      return {
        type: 'normal',
        ...TemplateFive.assets['bg-style-v1'],
        visible: true,
        name: 'bg',
        top: 204,
        left: 0
      }
    }

    return [
      [getBgAsset()],
      [
        {
          type: 'sprite',
          ...TemplateFive.assets['material-sprite'],
          visible: this.withStartCourse,
          name: 'bg-tpl_5_number_star',
          top: numberIndex[0][0],
          left: numberIndex[0][1],
          x: -173,
          y: -5,
          self: {
            width: 27,
            height: 27
          }
        },
        {
          type: 'sprite',
          ...TemplateFive.assets['material-sprite'],
          visible: this.realCurrent !== 1,
          name: 'bg-tpl_5_number_1',
          top: numberIndex[1][0],
          left: numberIndex[1][1],
          x: -173,
          y: -42,
          self: {
            width: 25,
            height: 25
          }
        },
        {
          type: 'sprite',
          ...TemplateFive.assets['material-sprite'],
          visible: this.realCurrent !== 2,
          name: 'bg-tpl_5_number_2',
          top: numberIndex[2][0],
          left: numberIndex[2][1],
          x: -173,
          y: -147,
          self: {
            width: 25,
            height: 25
          }
        },
        {
          type: 'sprite',
          ...TemplateFive.assets['material-sprite'],
          visible: this.realCurrent !== 3,
          name: 'bg-tpl_5_number_3',
          top: numberIndex[3][0],
          left: numberIndex[3][1],
          x: -173,
          y: -77,
          self: {
            width: 25,
            height: 25
          }
        },
        {
          type: 'sprite',
          ...TemplateFive.assets['material-sprite'],
          visible: this.realCurrent !== 4,
          name: 'bg-tpl_5_number_4',
          top: numberIndex[4][0],
          left: numberIndex[4][1],
          x: -210,
          y: -40,
          self: {
            width: 25,
            height: 25
          }
        },
        {
          type: 'sprite',
          ...TemplateFive.assets['material-sprite'],
          visible: this.realCurrent !== 5,
          name: 'bg-tpl_5_number_5',
          top: numberIndex[5][0],
          left: numberIndex[5][1],
          x: -210,
          y: -5,
          self: {
            width: 25,
            height: 25
          }
        },
        {
          type: 'sprite',
          ...TemplateFive.assets['material-sprite'],
          visible: this.withEndCourse,
          name: 'bg-tpl_5_number_star_end',
          top: numberIndex[6][0],
          left: numberIndex[6][1],
          x: -173,
          y: -5,
          self: {
            width: 27,
            height: 27
          }
        },
        {
          type: 'sprite',
          ...TemplateFive.assets['material-sprite'],
          visible: this.realCurrent > 0 && this.realCurrent < 6,
          name: 'bg-tpl_5_number_current',
          top: numberIndex[this.realCurrent][0],
          left: numberIndex[this.realCurrent][1],
          x: -173,
          y: -112,
          self: {
            width: 25,
            height: 25
          }
        }
      ]
    ]
  }

  public getClasshourLayoutInfo(_item: ClasshourItem, index: number): TopDirClasshourLayout | BottomDirClasshourLayout {
    switch (index) {
      case 0:
        return { directionStyle: 'bottom', bottom: 529, left: 108 } satisfies BottomDirClasshourLayout
      case 1:
        return { directionStyle: 'bottom', bottom: 554, left: 333 } satisfies BottomDirClasshourLayout
      case 2:
        return { directionStyle: 'bottom', bottom: 566, left: 775 } satisfies BottomDirClasshourLayout
      case 3:
        return { directionStyle: 'top', top: 580, left: 969 } satisfies TopDirClasshourLayout
      case 4:
        return { directionStyle: 'top', top: 613, left: 584 } satisfies TopDirClasshourLayout
      case 5:
        return { directionStyle: 'top', top: 573, left: 241 } satisfies TopDirClasshourLayout
      case 6:
        return { directionStyle: 'top', top: 484, left: 40 } satisfies TopDirClasshourLayout
      default:
        throw new Error('')
    }
  }
}
